import Dexie, { type EntityTable } from "dexie";

import type { RawRank } from "@tcg/ui";

/**
 * The tablet's IndexedDB (Dexie, design/05 §2/§4, T07-22). A CACHE and a QUEUE — never the source
 * of truth: prices, tax and totals always come from pos-service; a one-of-a-kind card may only be
 * sold offline when its availability was confirmed very recently (design/02 §7 P6).
 *
 * | table | what |
 * |---|---|
 * | `snapshotItems` | offline copy of what this register sells (`GET /pos/snapshot` NDJSON) — scan index by code / cert / JAN |
 * | `snapshotMeta` | version (watermark), fetchedAt, location of the copy |
 * | `carts` | the basket this register is working on (server id + version) — survives a reload / the lock screen |
 * | `opQueue` | offline operations (T07-33), each with its own `idempotencyKey` |
 * | `receiptsCache` | the last 200 printed receipts (ESC/POS bytes) — reprint while offline |
 * | `printJobs` | print queue (T07-25): failed prints stay here with 「再印刷」 |
 * | `settings` | printer / scanner / sound / locale of this tablet |
 * | `staffRecent` | who signed in on this tablet (id + name only — the PIN screen's name buttons) |
 * | `auth` | the device token, **encrypted** with a non-extractable WebCrypto key kept in this table (never localStorage) |
 * | `syncResults` | (v2, T07-33) what the server answered per offline operation: the real receipt number of an uploaded sale, and the 要確認 cards (conflict / rejected) until a manager closes them |
 */

export type ProductType = "raw_single" | "graded_single" | "sealed" | "supply" | string;

export interface Localized {
  ja?: string | null;
  en?: string | null;
}

export interface SnapshotItem {
  variantId: string;
  productId?: string | null;
  sku?: string | null;
  certNumber?: string | null;
  barcode?: string | null;
  title: Localized;
  variantTitle?: Localized | null;
  /** Integer JPY 税込 — display only; the server prices every line. */
  price: number;
  /** Shop Rank A–D — always set for raw singles. */
  rawRank?: RawRank | null;
  productType: ProductType;
  available: boolean;
  quantity: number;
  reason?: string | null;
  isConsigned: boolean;
  hasExternalListing: boolean;
  thumb?: string | null;
  updatedAt?: string | null;
  /** Epoch ms when the server last confirmed `available` (this tablet's fetch time). */
  availabilityConfirmedAt: number;
}

export interface SnapshotMeta {
  locationId: string;
  watermark: string | null;
  /**
   * `since=` of the next delta: the **server's** watermark of the last finished run (catalog
   * keeps it behind "now" so late commits are caught) — never the tablet's clock. null = full.
   */
  since: string | null;
  /** Where an unfinished run stopped (more pages than one run takes): resumed next time. */
  cursor?: string | null;
  fetchedAt: number;
  count: number;
}

export interface CartRef {
  registerId: string;
  cartId: string;
  version: number;
  updatedAt: number;
}

export type OpKind = "sale_cash" | "customer_quick_create" | "note";

export interface QueuedOp {
  id?: number;
  kind: OpKind;
  idempotencyKey: string;
  registerId: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
  /** ISO time with zone when it happened on the tablet (`clientTime` of the op). */
  clientTime?: string;
  /** What the offline receipt showed (for the queue screen): 仮番号, total, lines. */
  summary?: OpSummary;
}

export interface OpSummary {
  tempReceiptNo?: string;
  total?: number;
  tendered?: number;
  items?: Array<{ title: string; qty: number; price: number }>;
  staffName?: string | null;
}

export type SyncResultStatus = "ok" | "duplicate" | "conflict" | "rejected";

export interface SyncResultRecord {
  idempotencyKey: string;
  kind: OpKind;
  status: SyncResultStatus;
  /** Conflict / rejected sale: a manager must look at it (要確認). */
  needsReview: 0 | 1;
  saleId?: string | null;
  receiptNo?: string | null;
  orderNumber?: string | null;
  tempReceiptNo?: string | null;
  total?: number | null;
  reasons?: string[];
  errorCode?: string | null;
  errorMessage?: string | null;
  summary?: OpSummary;
  clientTime?: string;
  syncedAt: number;
  /** The manager confirmed it was handled in the back office (with their PIN). */
  resolvedAt?: number | null;
  resolvedBy?: string | null;
}

export interface CachedReceipt {
  receiptNo: string;
  saleId: string;
  /** ESC/POS base64 as the server sent it. */
  data: string;
  paperWidth: number;
  createdAt: number;
}

export type PrintJobStatus = "queued" | "printing" | "failed" | "done";

export interface PrintJob {
  id?: number;
  /** What was printed (receipt no. / "Z-report" …) — shown in the banner. */
  label: string;
  saleId?: string | null;
  receiptNo?: string | null;
  /** base64 ESC/POS bytes from the server (the client never renders receipt content). */
  data: string;
  /** Kick the cash drawer after printing (cash sale). */
  openDrawer: boolean;
  status: PrintJobStatus;
  attempts: number;
  lastError?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface Setting {
  key: string;
  value: unknown;
  updatedAt: number;
}

export interface RecentStaff {
  id: string;
  name: string;
  locale?: string | null;
  lastSeenAt: number;
}

export interface AuthRecord {
  key: string;
  value: unknown;
}

export class PosDatabase extends Dexie {
  snapshotItems!: EntityTable<SnapshotItem, "variantId">;
  snapshotMeta!: EntityTable<SnapshotMeta, "locationId">;
  carts!: EntityTable<CartRef, "registerId">;
  opQueue!: EntityTable<QueuedOp, "id">;
  receiptsCache!: EntityTable<CachedReceipt, "receiptNo">;
  printJobs!: EntityTable<PrintJob, "id">;
  settings!: EntityTable<Setting, "key">;
  staffRecent!: EntityTable<RecentStaff, "id">;
  auth!: EntityTable<AuthRecord, "key">;
  syncResults!: EntityTable<SyncResultRecord, "idempotencyKey">;

  constructor(name = "tcg-pos") {
    super(name);
    this.version(1).stores({
      snapshotItems: "variantId, sku, certNumber, barcode, productType, updatedAt",
      snapshotMeta: "locationId",
      carts: "registerId",
      opQueue: "++id, &idempotencyKey, kind, createdAt",
      receiptsCache: "receiptNo, saleId, createdAt",
      printJobs: "++id, status, createdAt",
      settings: "key",
      staffRecent: "id, lastSeenAt",
      auth: "key",
    });
    // T07-33: the server's answers to offline operations (real receipt numbers, 要確認)
    this.version(2).stores({
      syncResults: "idempotencyKey, status, needsReview, syncedAt",
    });
  }
}

export const db = new PosDatabase();

/** Most receipts kept for an offline reprint. */
export const RECEIPTS_CACHE_LIMIT = 200;

export async function cacheReceipt(database: PosDatabase, receipt: CachedReceipt): Promise<void> {
  await database.receiptsCache.put(receipt);
  const count = await database.receiptsCache.count();
  if (count > RECEIPTS_CACHE_LIMIT) {
    const old = await database.receiptsCache
      .orderBy("createdAt")
      .limit(count - RECEIPTS_CACHE_LIMIT)
      .primaryKeys();
    await database.receiptsCache.bulkDelete(old);
  }
}

/** Find a scanned code in the offline copy: label code → cert → JAN. */
export async function findInSnapshot(
  database: PosDatabase,
  code: string,
): Promise<SnapshotItem | undefined> {
  const value = code.trim();
  if (!value) {
    return undefined;
  }
  return (
    (await database.snapshotItems.where("sku").equals(value).first()) ??
    (await database.snapshotItems.where("certNumber").equals(value).first()) ??
    (await database.snapshotItems.where("barcode").equals(value).first())
  );
}

export async function readSetting<T>(database: PosDatabase, key: string, fallback: T): Promise<T> {
  const row = await database.settings.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function writeSetting(
  database: PosDatabase,
  key: string,
  value: unknown,
): Promise<void> {
  await database.settings.put({ key, value, updatedAt: Date.now() });
}

/** Single-unit availability older than this is unknown when offline (design/02 P6). */
export const SINGLE_UNIT_FRESHNESS_MS = 30_000;

const SINGLE_UNIT_TYPES: ReadonlySet<string> = new Set(["raw_single", "graded_single", "graded"]);

/**
 * P6: a one-of-a-kind card may be sold while offline only if the server confirmed it available
 * within the freshness window and it is not listed on another channel; sealed products (qty > 1)
 * may be sold and reconciled later.
 */
export function canSellOffline(
  item: Pick<
    SnapshotItem,
    "available" | "productType" | "availabilityConfirmedAt" | "hasExternalListing"
  >,
  now = Date.now(),
  freshnessMs = SINGLE_UNIT_FRESHNESS_MS,
): boolean {
  if (!item.available) {
    return false;
  }
  if (!SINGLE_UNIT_TYPES.has(item.productType)) {
    return true;
  }
  if (item.hasExternalListing) {
    return false;
  }
  return now - item.availabilityConfirmedAt <= freshnessMs;
}

/** The UI language one staff member chose on this tablet (設定 → 言語, T07-34). */
export function staffLocaleKey(staffId: string): string {
  return `locale:${staffId}`;
}
